import React from "react";
import { Box, CloseButton, Flex, useToast } from "@chakra-ui/react";
import Container from "@material-ui/core/Container";
import CssBaseline from "@material-ui/core/CssBaseline";
import Paper from "@material-ui/core/Paper";
import axios from "axios";
import ChakraInput from "components/Misc/FormComponents/ChakraInput";
import ChakraRadio from "components/Misc/FormComponents/ChakraRadio";
import ChakraTextArea from "components/Misc/FormComponents/ChakraTextArea";
import FileUpload from "components/Misc/FormComponents/FileUpload";
import { Field, Form, Formik } from "formik";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import { api, UploadApi } from "../../config";
import { Announcement, Group, Student } from "../../types/apiTypes";
import SelectFormFieldChip from "../Misc/FormComponents/SelectFormFieldChip";
import SubmitBtn from "../Misc/FormComponents/SubmitBtn";
import announcementStyles from "./styles/groupStyles";
import { useQuery } from "@tanstack/react-query";

// form validations
const schema = yup.object({
  title: yup.string().required("Title is Required"),
  phoneNumbers: yup.string().when("msgType", {
    is: "WA",
    then: yup
      .string()
      .test("valid-phone", "One or more phone numbers are invalid", (value) => {
        if (!value) return true;
        const phoneNumbers = value.split(",").map((num: string) => num.trim());
        const phoneRegex = /^[0-9]{1,14}$/; // Adjust the regex if needed
        return phoneNumbers.every((num: string) => phoneRegex.test(num));
      }),
  }),
});

interface Props {
  announcement?: Announcement;
}

const AnnouncementForm: React.FC<Props> = ({ announcement }) => {
  const isEdit = typeof announcement !== "undefined";
  const history = useHistory();
  const toast = useToast();
  const [loading, setLoading] = React.useState<boolean>(false);

  const classes = announcementStyles();

  const { data: groups = [] } = useQuery<Group[]>({
    queryKey: ["groups"],
    queryFn: async () => {
      const res = await axios.get(`${api}/groups`, { withCredentials: true });
      return res.data;
    },
  });

  const { data: students = [] } = useQuery({
    queryKey: ["students"],
    queryFn: async () => {
      const res = await axios.get(`${api}/users?role=1`, {
        withCredentials: true,
      });
      return res.data.map((student: any) => ({
        label: student.username,
        value: student.id,
      }));
    },
  });

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      let stuIDS: any = [];
      if (values.groups.length > 0) {
        for (let i = 0; i < values.groups.length; i++) {
          // @ts-ignore
          const stuIds = groups
            .find((group: Group) => group.id === values.groups[i])
            .users_permissions_users.map((student: Student) => student.id);
          stuIDS = [...stuIDS, ...stuIds];
          // console.log(stuIds);
        }
      }

      if (values.students.length > 0) stuIDS = [...stuIDS, ...values.students];
      stuIDS = [...new Set(stuIDS)];
      if (stuIDS.length === 0 && values.msgType !== "WA") {
        toast({
          title: "Please select at least one student or group",
          status: "error",
          position: "bottom-right",
        });
        return;
      }
      if (
        values.msgType === "WA" &&
        stuIDS.length === 0 &&
        !values.phoneNumbers
      ) {
        toast({
          title: "Please add any phone number",
          status: "error",
          position: "bottom-right",
        });
        return;
      }
      if (values.msgType === "WA") {
        const data = {
          message: values.message,
          userIdList: stuIDS,
          phoneNumbers: values.phoneNumbers?.split(","),
          document: values.document ? `${UploadApi}${values.document}` : null,
        };
        const res = await axios.post(
          `${api}/announcements/send-whatsapp-student-list`,
          data,
          {
            withCredentials: true,
          },
        );
        if (res.data.error) {
          throw new Error(res.data.error);
        }
        toast({
          title: `WhatsApp Message Sent Successfully`,
          position: "bottom-right",
          status: "success",
        });
      } else {
        const data = stuIDS.map((id: number) => {
          return {
            messageCode: values.messageCode,
            title: values.title,
            message: values.message,
            users_permissions_user: id,
          };
        });
        // console.log(data);
        await axios.post(`${api}/announcements`, data, {
          withCredentials: true,
        });
        toast({
          title: `Announcement ${!isEdit ? "Added" : "Updated"} Succefully`,
          position: "bottom-right",
          status: "success",
        });
      }
      history.push("/admin/announcements");
    } catch (error: any) {
      console.log(error);
      console.log(error.response.data.message[0].messages[0].message);
      toast({
        title: error.response.data.message[0].messages[0].message,
        position: "bottom-right",
        status: "error",
      });
    }
    setLoading(false);
  };

  return (
    <div>
      <Paper className={classes.paper}>
        <Container component="main">
          <CssBaseline />
          <div className={classes.paper}>
            <Formik
              onSubmit={(values) => handleSubmit(values)}
              initialValues={{
                messageCode: isEdit
                  ? announcement?.messageCode
                  : Math.random().toString(36).slice(-8),
                title: isEdit ? announcement?.title : "",
                message: isEdit ? announcement?.message : "",
                groups: [],
                students: [],
                msgType: "AN",
                document: "",
              }}
              validationSchema={schema}
            >
              {({ values, setFieldValue }) => (
                <Form className={classes.form}>
                  <h3 className={classes.heading}>
                    {isEdit ? "View" : "Add"} Announcement
                  </h3>
                  <Field
                    name="msgType"
                    component={ChakraRadio}
                    label="Message Type"
                    type="radio"
                    options={[
                      { label: "Announcement", value: "AN" },
                      { label: "WhatsApp", value: "WA" },
                    ]}
                    defaultValue={values.msgType}
                    onChange={(choice: string) => {
                      setFieldValue("msgType", choice);
                      if (choice === "WA") {
                        setFieldValue("title", "WhatsApp Message");
                      }
                    }}
                  />
                  <Field
                    name="messageCode"
                    component={ChakraInput}
                    label="Announcement Code"
                    fullWidth
                    disabled={true}
                  />
                  <Field
                    name="title"
                    component={ChakraInput}
                    label="Title"
                    fullWidth
                    disabled={isEdit || values.msgType === "WA"}
                  />
                  <Field
                    name="message"
                    component={ChakraTextArea}
                    label="Message"
                    multiline
                    disabled={isEdit}
                  />
                  {values.msgType === "WA" && (
                    <>
                      <Flex direction={"row"} align={"center"}>
                        <FileUpload
                          text="Upload Image/PDF"
                          handleSetFieldValue={setFieldValue}
                          handleFieldName={`document`}
                        />
                        {typeof values.document === "string" &&
                        values.document.length > 0 ? (
                          <>
                            <Box marginLeft={"3"}>
                              {values.document.split("/")[2] || ""}
                            </Box>
                            <CloseButton
                              color="red"
                              onClick={() => setFieldValue("document", "")}
                            />
                          </>
                        ) : (
                          ""
                        )}
                      </Flex>
                      <Field
                        name="phoneNumbers"
                        component={ChakraInput}
                        label="Additional WhatsApp Phone Numbers with country code (Comma Separated)"
                        fullWidth
                      />
                    </>
                  )}
                  <Field
                    name="groups"
                    component={SelectFormFieldChip}
                    label="Groups"
                    placeholder="Groups"
                    multiple
                    options={groups.map((group: any) => ({
                      label: group.groupName,
                      value: group.id,
                    }))}
                    variant="outlined"
                    disabled={isEdit}
                  />
                  <Field
                    name="students"
                    component={SelectFormFieldChip}
                    label="Students"
                    placeholder="Students"
                    multiple
                    options={students}
                    variant="outlined"
                    disabled={isEdit}
                  />
                  {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
                  <SubmitBtn
                    loading={loading}
                    w="100%"
                    my="3"
                    disabled={isEdit}
                  />
                </Form>
              )}
            </Formik>
          </div>
        </Container>
      </Paper>
    </div>
  );
};

export default AnnouncementForm;
